import {createSlice} from '@reduxjs/toolkit';

import {displayDateFormat} from 'utils/date-time';
import dayjs from 'utils/dayjs';
import {type ObjectType} from '../utils/transform-changes';

export interface ChangelogFilterValues {
    dateFrom: string;
    dateTo: string;
    changeTypes: ObjectType[];
    numFiltersApplied: number;
}

export interface ChangelogState {
    filters: ChangelogFilterValues;
}

export const getDefaultDateRange = () => ({
    dateFrom: dayjs().subtract(3, 'months').format(displayDateFormat),
    dateTo: dayjs().format(displayDateFormat),
});

export const initialState: ChangelogState = {
    filters: {
        ...getDefaultDateRange(),
        changeTypes: [],
        numFiltersApplied: 0,
    },
};

const changelogSlice = createSlice({
    name: 'changelog',
    initialState,
    reducers: {
        updateFilters: {
            reducer: (state, {payload}: {payload: ChangelogFilterValues}) => {
                // Keeps the existing changeTypes reference when its content
                // is unchanged, so a no-op Apply doesn't look like a new
                // filter to callers that depend on it by reference.
                const changeTypesUnchanged =
                    state.filters.changeTypes.length ===
                        payload.changeTypes.length &&
                    payload.changeTypes.every((changeType) =>
                        state.filters.changeTypes.includes(changeType)
                    );

                state.filters = {
                    ...payload,
                    changeTypes: changeTypesUnchanged
                        ? state.filters.changeTypes
                        : payload.changeTypes,
                };
            },
            prepare: (
                payload: Omit<ChangelogFilterValues, 'numFiltersApplied'>
            ) => {
                const defaultDateRange = getDefaultDateRange();
                let appliedFiltersCount = 0;

                if (payload.dateFrom !== defaultDateRange.dateFrom) {
                    appliedFiltersCount++;
                }

                if (payload.dateTo !== defaultDateRange.dateTo) {
                    appliedFiltersCount++;
                }

                // Each selected entity-type value counts as its own applied
                // filter. The default selection is always empty, so every
                // selected value is by definition an applied filter.
                appliedFiltersCount += payload.changeTypes.length;

                return {
                    payload: {
                        ...payload,
                        numFiltersApplied: appliedFiltersCount,
                    },
                };
            },
        },
    },
});

export const {updateFilters} = changelogSlice.actions;
export default changelogSlice.reducer;
